import { displayUnitOf } from "@heraion/shared";
import { useEffect, useState } from "react";
import { ScrollView } from "react-native";
import type { Plan, Program } from "@heraion/shared";
import {
  LibraryBrowser,
  ProgramComposer,
  resolveTheme,
  useCatalogItems,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { confirmViaWindow } from "./confirm";
import { durableWrite } from "./durableWrite";
import { ExercisePickerMount } from "./ExercisePickerMount";
import { useLoggerProfile } from "./logger/useLoggerProfile";
import { PlanDateField } from "./PlanDateField";
import type { ComposerRequest } from "./useCreateHubWeb";
import { useProfileDoc } from "./useProfileDoc";
import { useAccountKey } from "./auth/useAccountKey";
import { useProgramEditorData } from "./useProgramEditorData";
import { useThemePalette } from "./theme";

// CO-19 - the coach experience's own Library surface (AppShell's coach
// nav tabs), web's twin of mobile's CoachLibraryScreen. `authoredAs` is
// constant "coach" throughout this file: this mount only ever renders while
// `experience === "coach"` (AppShell gates it), so there is no other view to
// disambiguate from - unlike PlanScreen's ProgramComposer mounts (athlete's
// surface, never reached from here since AppShell now routes coach's hub
// requests to "library" instead of "plan"/"workouts").
// Same self-contained edit/create-via-local-state shape as PlanScreen: rows
// picked from the LibraryBrowser below open the composer locally; the
// Create-hub's program/plan actions arrive cross-surface as a
// composerRequest (same seam PlanScreen consumes), because the hub is a
// modal over whichever tab is active.
export function CoachLibraryScreen({
  composerRequest,
  onComposerRequestHandled,
  callerSub,
  athleteRoster,
}: {
  composerRequest?: ComposerRequest | null;
  onComposerRequestHandled?: () => void;
  callerSub?: string;
  athleteRoster?: ReadonlyArray<string>;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const catalog = useCatalogItems({ catalog: exerciseCatalog });
  const { doc } = useProfileDoc();
  const units = displayUnitOf(doc);
  const loggerProfile = useLoggerProfile();
  // #336: the editors' device drafts, per account on this browser. The
  // library below remounts when an editor closes, so it refetches by itself.
  const draftScope = useAccountKey("editorDraft");

  const [editing, setEditing] = useState<Program | null>(null);
  // #336 slice 6: the program editor's progress marker and MAXes.
  const editorData = useProgramEditorData(editing?.id);
  const [creating, setCreating] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [creatingPlan, setCreatingPlan] = useState(false);

  // Same consume-then-clear contract as PlanScreen's composerRequest effect.
  useEffect(() => {
    if (!composerRequest) return;
    if (composerRequest.kind === "program") {
      if (composerRequest.program) setEditing(composerRequest.program);
      else setCreating(true);
    } else if (composerRequest.kind === "plan") {
      if (composerRequest.plan) setEditingPlan(composerRequest.plan);
      else setCreatingPlan(true);
    }
    // "workout" requests never route here (AppShell only sends this surface
    // program/plan kinds) — nothing for this effect to do with one.
    onComposerRequestHandled?.();
  }, [composerRequest, onComposerRequestHandled]);

  if (editing || creating) {
    return (
      <ProgramComposer
        key={editing?.id ?? "new"}
        mode="prescribe"
        profile={loggerProfile}
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        program={editing ?? undefined}
        {...editorData}
        authoredAs="coach"
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        unitsPreference={units}
        confirm={confirmViaWindow}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        keepOpen
        draftScope={draftScope}
        draftOwner={callerSub ?? null}
        onSaved={() => {
          setEditing(null);
          setCreating(false);
        }}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
      />
    );
  }

  if (editingPlan || creatingPlan) {
    return (
      <ProgramComposer
        key={editingPlan?.id ?? "new-plan"}
        mode="single"
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        dark={dark}
        plan={editingPlan ?? undefined}
        authoredAs="coach"
        catalog={catalog.items}
        Picker={ExercisePickerMount}
        DateField={PlanDateField}
        unitsPreference={units}
        confirm={confirmViaWindow}
        callerSub={callerSub}
        athleteRoster={athleteRoster}
        keepOpen
        draftScope={draftScope}
        draftOwner={callerSub ?? null}
        onSaved={() => {
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
        onCancel={() => {
          setEditingPlan(null);
          setCreatingPlan(false);
        }}
      />
    );
  }

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme.palette.bg }}
      showsVerticalScrollIndicator={false}
    >
      <LibraryBrowser
        theme={theme}
        dark={dark}
        apiFetch={apiFetch}
        durableWrite={durableWrite}
        catalog={catalog.raw ? [...catalog.raw] : null}
        unit={units}
        authoredAs="coach"
        callerSub={callerSub ?? null}
        athleteRoster={athleteRoster}
        onEditProgram={setEditing}
        onEditPlan={setEditingPlan}
      />
    </ScrollView>
  );
}
